// Connect (or reconnect) a Titan mailbox — MA-55.
//
// Titan has no OAuth, so this is a credentials form rather than Gmail's redirect. The backend logs
// in to Titan before it stores anything, so a wrong password comes straight back here with Titan's
// setup hints and nothing is left behind half-connected.
//
// The form's state and its one request live in `useTitanConnectionForm`, apart from the markup, so
// they can be tested without Radix (see `__tests__/TitanConnectionDialog.test.jsx`).

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@mantis-sas/ui";
import { connectTitanAccount } from "../../../../ApiCalls/mantisBusiness";
import {
  TITAN_REGIONS,
  titanConnectPayload,
  titanErrorMessage,
  validateTitanForm,
} from "../../../../utils/titanConnections";

const EMPTY = { email: "", password: "", region: "global" };

export function useTitanConnectionForm({ token, onConnected }) {
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setField = useCallback((name, value) => {
    setValues((previous) => ({ ...previous, [name]: value }));
  }, []);

  // The password never outlives the dialog: every open and every close starts from here.
  const reset = useCallback((next = {}) => {
    setValues({ ...EMPTY, ...next, password: "" });
    setError(null);
    setSubmitting(false);
  }, []);

  const submit = async () => {
    const invalid = validateTitanForm(values);
    if (invalid) {
      setError(invalid);
      return false;
    }

    setSubmitting(true);
    setError(null);
    let response;
    try {
      response = await connectTitanAccount(token, titanConnectPayload(values));
    } catch (requestError) {
      response = null;
    }

    const message = titanErrorMessage(response);
    if (message) {
      // What was typed stays, so a typo can be fixed without typing it all again.
      setError(message);
      setSubmitting(false);
      return false;
    }

    setValues((previous) => ({ ...previous, password: "" }));
    setSubmitting(false);
    onConnected(response.connection, Boolean(response.reconnected));
    return true;
  };

  return { values, setField, error, submitting, submit, reset };
}

const selectClassName =
  "flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 " +
  "dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";

const hintClassName = "text-xs text-slate-500 dark:text-slate-400";

function TitanConnectionDialog({
  open,
  onOpenChange,
  token,
  initialEmail = "",
  initialRegion = "global",
  onConnected,
}) {
  const form = useTitanConnectionForm({
    token,
    onConnected: (connection, reconnected) => {
      onConnected(connection, reconnected);
      onOpenChange(false);
    },
  });
  const { reset } = form;

  useEffect(() => {
    reset(open ? { email: initialEmail, region: initialRegion } : {});
  }, [open, initialEmail, initialRegion, reset]);

  const close = (next) => {
    if (!form.submitting) onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {initialEmail ? "Reconectar buzón de Titan" : "Conectar buzón de Titan"}
          </DialogTitle>
          <DialogDescription>
            Mantis leerá los correos que lleguen a la bandeja de entrada, con sus adjuntos. No
            marca nada como leído ni envía correos desde este buzón.
          </DialogDescription>
        </DialogHeader>

        <form
          id="titan-connection-form"
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            form.submit();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="titan-email">Correo</Label>
            <Input
              id="titan-email"
              type="email"
              autoComplete="off"
              placeholder="ventas@tuempresa.com"
              value={form.values.email}
              onChange={(event) => form.setField("email", event.target.value)}
              disabled={form.submitting || Boolean(initialEmail)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="titan-password">Contraseña</Label>
            <Input
              id="titan-password"
              type="password"
              // Not the person's Mantis password, so the browser must not offer it here.
              autoComplete="new-password"
              value={form.values.password}
              onChange={(event) => form.setField("password", event.target.value)}
              disabled={form.submitting}
            />
            <p className={hintClassName}>
              Si tienes verificación en dos pasos en Titan, crea una contraseña de aplicación y
              úsala aquí.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="titan-region">Proveedor</Label>
            <select
              id="titan-region"
              className={selectClassName}
              value={form.values.region}
              onChange={(event) => form.setField("region", event.target.value)}
              disabled={form.submitting}
            >
              {TITAN_REGIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className={hintClassName}>
              Dónde compraste el correo. Si fue en GoDaddy, elige GoDaddy: su correo Titan está
              en otro servidor. Elige proveedor europeo solo si fue Blacknight, MonoVM,
              DomainDiscount24, Fluccs o Domain Central.
            </p>
          </div>

          <p className={hintClassName}>
            Antes de conectar, activa el acceso de aplicaciones de terceros en la configuración
            de Titan.
          </p>

          {form.error && (
            <Alert variant="destructive">
              <AlertDescription>{form.error}</AlertDescription>
            </Alert>
          )}
        </form>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => close(false)}
            disabled={form.submitting}
          >
            Cancelar
          </Button>
          <Button type="submit" form="titan-connection-form" disabled={form.submitting}>
            {form.submitting ? "Verificando con Titan…" : "Conectar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TitanConnectionDialog;
